// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { makeHandlerChainAwaitable } from "@insite/client-framework/HandlerCreator";
import siteMessage from "@insite/client-framework/SiteMessage";
import { getAddressFieldsDataView } from "@insite/client-framework/Store/Data/AddressFields/AddressFieldsSelector";
import { getCurrentCountries } from "@insite/client-framework/Store/Data/Countries/CountriesSelectors";
import loadCurrentCountries from "@insite/client-framework/Store/Data/Countries/Handlers/LoadCurrentCountries";
import translate from "@insite/client-framework/Translate";
import { ShipToModel } from "@insite/client-framework/Types/ApiModels";
import ReCaptcha from "@insite/content-library/Components/ReCaptcha";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Checkbox, { CheckboxPresentationProps, CheckboxProps } from "@insite/mobius/Checkbox";
import LoadingOverlay from "@insite/mobius/LoadingOverlay";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import AddressValidationModal from "@rpsc/App/Components/Addresses/AddressValidationModal";
import CreateAccountBillingAddressForm from "@rpsc/App/Components/CreateAccount/CreateAccountBillingAddressForm";
import CreateAccountShippingAddressForm from "@rpsc/App/Components/CreateAccount/CreateAccountShippingAddressForm";
import setInitialValues from "@rpsc/App/Handlers/CreateAccount/SetInitialValues";
import AddressValidation, { AddressValidationHandlerResult } from "@rpsc/App/Handlers/XNG/AddressValidation";
import { AddressValidationApiParameter } from "@rpsc/App/Services/AddressValidation";
import { CustomAppState, xCreateAccountActions } from "@rpsc/App/Store/Reducers";
import { getSettingsCollection } from "@rpsc/App/Store/Selectors/SettingsSelectors";
import { FORM_IDS } from "@rpsc/XDL/constants";
import { xValidateOnSubmit } from "@rpsc/XDL/Utilities";
import React, { RefObject, useEffect, useState } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import { connect, ResolveThunks, useDispatch, useStore } from "react-redux";
import { Dispatch } from "redux";

type OwnProps = {
    onSubmit: () => void;
    onBackClick: () => void;
    termsAndConditions?: any;
    isFormSubmitting?: boolean;
    submitBtnLabel?: string;
};

const mapStateToProps = (state: CustomAppState) => ({
    countries: getCurrentCountries(state),
    billToAddressFields: getAddressFieldsDataView(state).value?.billToAddressFields,
    shipToAddressFields: getAddressFieldsDataView(state).value?.shipToAddressFields,
    billToValidation: getSettingsCollection(state).billToValidation,
    shipToValidation: getSettingsCollection(state).shipToValidation,
    shipToSameAsBillTo: state.xCreateAccount.accountInfo.billingAndShippingInfo.shipToSameAsBillTo,
});

const mapDispatchToProps = {
    loadCurrentCountries,
    setInitialValues,
    validateAddress: makeHandlerChainAwaitable<Parameters<typeof AddressValidation>[0], AddressValidationHandlerResult>(
        AddressValidation,
    ),
    setShipToAddress: (address: ShipToModel) => (dispatch: Dispatch<xCreateAccountActions>) => {
        dispatch({
            type: "xCreateAccount/SetShippingAddress",
            address,
        });
    },
};

type Props = OwnProps & ResolveThunks<typeof mapDispatchToProps> & ReturnType<typeof mapStateToProps>;

export interface CreateAccountBillingAndShippingInfoStyles {
    wrapper?: InjectableCss;
    addressFormWrapper?: InjectableCss;
    titleText?: TypographyPresentationProps;
    useBillingAddressWrapper?: InjectableCss;
    useBillingAddressCheckbox?: CheckboxPresentationProps;
    recaptchaWrapper?: InjectableCss;
    infoWrapper?: InjectableCss;
    buttonsWrapper?: InjectableCss;
    backButton?: ButtonPresentationProps;
    submitButton?: ButtonPresentationProps;
}

export const createAccountBillingAndShippingInfoStyles: CreateAccountBillingAndShippingInfoStyles = {};

const styles = createAccountBillingAndShippingInfoStyles;

const CreateAccountBillingAndShippingInfo = ({
    countries,
    billToAddressFields,
    shipToAddressFields,
    billToValidation,
    shipToValidation,
    termsAndConditions,
    isFormSubmitting,
    submitBtnLabel,
    shipToSameAsBillTo,
    onSubmit,
    onBackClick,
    loadCurrentCountries,
    setInitialValues,
    validateAddress,
    setShipToAddress,
}: Props) => {
    const dispatch = useDispatch();
    const [isLoading, setIsLoading] = useState(false);
    const [openAddressValidationModal, setOpenAddressValidationModal] = useState(false);
    const [addressValidationResult, setAddressValidationResult] = useState({} as AddressValidationHandlerResult);
    const store = useStore();

    const recaptchaRef: RefObject<ReCAPTCHA> = React.createRef();

    useEffect(() => {
        if (!countries) {
            loadCurrentCountries();
        }
    }, []);

    useEffect(() => {
        setInitialValues({
            country: countries
                ? countries.find(country => country.abbreviation?.toLowerCase().startsWith("us")) || countries[0]
                : undefined,
            billToValidation,
            shipToValidation,
        });
    }, [countries]);

    const useShippingAddressChangeHandler: CheckboxProps["onChange"] = (_, value) => {
        dispatch({
            type: "xCreateAccount/IsShipToSameAsBillTo",
            isShipToSameAsBillTo: value,
        });
    };

    if (!countries) {
        return null;
    }

    const submitHandler = async (skipValidation = false) => {
        setIsLoading(true);
        const errors = xValidateOnSubmit(FORM_IDS.BILLING_ADDRESS);
        if (!shipToSameAsBillTo) {
            errors.push(...xValidateOnSubmit(FORM_IDS.SHIPPING_ADDRESS));
        }
        if (errors.length > 0) {
            setIsLoading(false);
            return;
        }

        // Add Custom Validation
        if (!skipValidation) {
            // instead of using refs. to not have any performance issue.
            const { shipToSameAsBillTo, billTo, shipTo } = (store.getState() as CustomAppState).xCreateAccount
                .accountInfo.billingAndShippingInfo;
            const _shipToAddress = shipToSameAsBillTo
                ? { ...billTo, state: billTo.state?.abbreviation, country: billTo.country?.abbreviation }
                : { ...shipTo, state: shipTo.state?.abbreviation, country: shipTo.country?.abbreviation };

            const result = await validateAddress({
                ...(_shipToAddress as AddressValidationApiParameter),
            });

            if (!result.skipValidation && !result.addressResultDto?.isMatch) {
                // when the validation is on
                setIsLoading(false);
                setAddressValidationResult(result);
                setOpenAddressValidationModal(true);
                return;
            }
        }
        setIsLoading(false);
        onSubmit();
    };

    return (
        <LoadingOverlay loading={isLoading}>
            <StyledWrapper {...styles.wrapper}>
                {billToAddressFields && (
                    <StyledWrapper {...styles.addressFormWrapper}>
                        <Typography {...styles.titleText}>{translate("Billing Address")}</Typography>
                        <CreateAccountBillingAddressForm
                            countries={countries}
                            fieldDisplay={billToAddressFields}
                            formId={FORM_IDS.BILLING_ADDRESS}
                        />
                    </StyledWrapper>
                )}
                {shipToAddressFields && (
                    <StyledWrapper {...styles.addressFormWrapper}>
                        <Typography {...styles.titleText}>{translate("Shipping Address")}</Typography>
                        <StyledWrapper {...styles.useBillingAddressWrapper}>
                            <Checkbox
                                {...styles.useBillingAddressCheckbox}
                                onChange={useShippingAddressChangeHandler}
                                checked={shipToSameAsBillTo}
                                data-test-selector="useShippingAddressCheckbox"
                            >
                                {translate("Use Billing Address")}
                            </Checkbox>
                        </StyledWrapper>
                        {!shipToSameAsBillTo && (
                            <CreateAccountShippingAddressForm
                                countries={countries}
                                fieldDisplay={shipToAddressFields}
                                formId={FORM_IDS.SHIPPING_ADDRESS}
                            />
                        )}
                    </StyledWrapper>
                )}
            </StyledWrapper>
            <StyledWrapper {...styles.recaptchaWrapper}>
                <ReCaptcha recaptchaRef={recaptchaRef} location="CreateAccount" />
            </StyledWrapper>
            <StyledWrapper {...styles.infoWrapper}>
                <Typography>{siteMessage("Custom_CreateAccount_NetNew_GeneralMsg")}</Typography>
            </StyledWrapper>
            <StyledWrapper {...styles.infoWrapper}>{termsAndConditions}</StyledWrapper>
            <StyledWrapper {...styles.buttonsWrapper}>
                <Button {...styles.backButton} onClick={onBackClick} aria-label={translate("Back")}>
                    {translate("Back")}
                </Button>
                <Button
                    {...styles.submitButton}
                    onClick={() => submitHandler()}
                    disabled={isFormSubmitting}
                    aria-label={submitBtnLabel || translate("Submit")}
                >
                    {submitBtnLabel || translate("Submit")}
                </Button>
            </StyledWrapper>
            <AddressValidationModal
                selector={state => {
                    const { shipToSameAsBillTo, billTo, shipTo } =
                        state.xCreateAccount.accountInfo.billingAndShippingInfo;
                    const shipToAddress = shipToSameAsBillTo ? billTo : shipTo;
                    return shipToAddress as ShipToModel;
                }}
                isOpen={openAddressValidationModal}
                setIsOpen={setOpenAddressValidationModal}
                updateAddress={address => {
                    dispatch({
                        type: "xCreateAccount/IsShipToSameAsBillTo",
                        isShipToSameAsBillTo: false,
                    });
                    setShipToAddress(address);
                }}
                onAcceptSuggestion={() => {
                    submitHandler(true);
                }}
                onEdit={() => setOpenAddressValidationModal(false)}
                suggestedAddressResult={addressValidationResult}
                onUseCurrentAddress={() => {
                    submitHandler(true);
                }}
            />
        </LoadingOverlay>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(CreateAccountBillingAndShippingInfo);
